// Step 27 follow-up: capture moments the LLM MISSED.
//
// The ✓ Approve flow only works on moments the LLM emitted. When the
// editor spots a real reel-worthy moment the LLM never produced (a missed
// goal, an uncalled save, etc.), this dialog lets them add it directly to
// `approved_moments` as a positive gold label. The eval grader then counts
// the resulting false-negative correctly.
//
// Submits via the same `useApprove` hook that ✓ Approve uses — the
// synthesized moment has `source: 'editor_manual'`, `detector: null`.

import { useState } from 'react'
import { Plus, X } from 'lucide-react'

// Same enum as the LLM tool schema in tbtv-highlights/src/providers/tbtv-ai.js
// and the gold-set import script. Keep in sync.
const MOMENT_TYPES = [
  'goal', 'save', 'shot', 'fight', 'penalty',
  'power_play', 'penalty_kill', 'defense', 'faceoff',
  'structural', 'narrative', 'ceremony',
]

// Parses mm:ss, hh:mm:ss, or bare seconds (decimals OK). Returns ms or null.
function parseTimestamp(raw) {
  if (raw == null) return null
  const t = String(raw).trim()
  if (!t) return null
  let m = t.match(/^(\d+):(\d+):(\d+(?:\.\d+)?)$/)
  if (m) return Math.round((parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60 + parseFloat(m[3])) * 1000)
  m = t.match(/^(\d+):(\d+(?:\.\d+)?)$/)
  if (m) return Math.round((parseInt(m[1], 10) * 60 + parseFloat(m[2])) * 1000)
  if (/^\d+(?:\.\d+)?$/.test(t)) return Math.round(parseFloat(t) * 1000)
  return null
}

export default function AddMissingMomentDialog({ tbtvId, videoTitle, onCancel, onSubmit }) {
  const [timestamp, setTimestamp] = useState('')
  const [momentType, setMomentType] = useState('goal')
  const [description, setDescription] = useState('')
  const [scorer, setScorer] = useState('')
  const [parseErr, setParseErr] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const startMs = parseTimestamp(timestamp)
  const valid = startMs != null && !!momentType && description.trim().length > 0

  async function handleSubmit() {
    if (!valid || submitting) return
    setParseErr(null)
    if (startMs == null) {
      setParseErr('Timestamp must be mm:ss, hh:mm:ss, or bare seconds')
      return
    }
    setSubmitting(true)
    try {
      // Synthesize a moment shaped for the useApprove hook + the backend
      // approveMoment endpoint. end_ms defaulted to start + 15s — purely
      // for display; grader matches on (tbtv_id, moment_type, start_ms ±10s).
      await onSubmit({
        tbtv_id: tbtvId,
        start_ms: startMs,
        end_ms: startMs + 15000,
        moment_type: momentType,
        title: description.trim() + (scorer.trim() ? ` (${scorer.trim()})` : ''),
        source: 'editor_manual',
        detector: null,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10001,
      background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8,
        padding: 18, width: 'min(480px, 92vw)', display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} style={{ color: '#4ade80' }} />
          <div style={{ fontSize: 13, color: '#86efac', fontWeight: 600, flex: 1 }}>
            Add missing moment
          </div>
          <button onClick={onCancel} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 0 }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#888', lineHeight: 1.5 }}>
          For real reel-worthy moments the LLM didn't catch. Adds directly to the eval gold set + future RAG exemplars.
        </div>
        <div style={{ fontSize: 11, color: '#666', fontFamily: 'monospace', borderBottom: '1px solid #1e293b', paddingBottom: 6 }}>
          {videoTitle || tbtvId}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: 11, color: '#aaa' }}>
            Timestamp
            <input
              type="text"
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              placeholder="e.g. 42:35 or 1:23:45"
              autoFocus
              style={{
                width: '100%', padding: '6px 8px', fontSize: 12, marginTop: 4,
                background: '#0d1622', border: '1px solid ' + (timestamp && startMs == null ? '#dc2626' : '#1e293b'),
                borderRadius: 4, color: '#ddd', fontFamily: 'monospace',
              }}
            />
            {timestamp && startMs != null && (
              <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
                Will record at {Math.floor(startMs / 1000)}s = {fmtPreview(startMs)}
              </div>
            )}
          </label>

          <label style={{ fontSize: 11, color: '#aaa' }}>
            Type
            <select
              value={momentType}
              onChange={(e) => setMomentType(e.target.value)}
              style={{
                width: '100%', padding: '6px 8px', fontSize: 12, marginTop: 4,
                background: '#0d1622', border: '1px solid #1e293b', borderRadius: 4, color: '#ddd',
              }}>
              {MOMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>

          <label style={{ fontSize: 11, color: '#aaa' }}>
            Description
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Smith breakaway goal — 1-0 West"
              style={{
                width: '100%', padding: '6px 8px', fontSize: 12, marginTop: 4,
                background: '#0d1622', border: '1px solid #1e293b', borderRadius: 4, color: '#ddd',
              }}
            />
          </label>

          <label style={{ fontSize: 11, color: '#aaa' }}>
            Scorer <span style={{ color: '#666' }}>(optional)</span>
            <input
              type="text"
              value={scorer}
              onChange={(e) => setScorer(e.target.value)}
              placeholder="canonical surname"
              style={{
                width: '100%', padding: '6px 8px', fontSize: 12, marginTop: 4,
                background: '#0d1622', border: '1px solid #1e293b', borderRadius: 4, color: '#ddd',
              }}
            />
          </label>
        </div>

        {parseErr && <div style={{ fontSize: 11, color: '#fca5a5' }}>{parseErr}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onCancel}
            style={{
              padding: '6px 12px', fontSize: 12,
              background: 'transparent', border: '1px solid #333', borderRadius: 4,
              color: '#bfdbfe', cursor: 'pointer',
            }}>Cancel</button>
          <button onClick={handleSubmit} disabled={!valid || submitting}
            style={{
              padding: '6px 14px', fontSize: 12,
              background: valid && !submitting ? '#14532d' : '#222',
              border: '1px solid ' + (valid && !submitting ? '#16a34a' : '#333'),
              borderRadius: 4,
              color: valid && !submitting ? '#86efac' : '#555',
              cursor: valid && !submitting ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
            <Plus size={12} /> {submitting ? 'Adding…' : 'Add as gold label'}
          </button>
        </div>
      </div>
    </div>
  )
}

function fmtPreview(ms) {
  const totalS = Math.floor((ms || 0) / 1000)
  const h = Math.floor(totalS / 3600)
  const m = Math.floor((totalS % 3600) / 60)
  const s = totalS % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}
